/**
 * §1 Baseline 2025 — the four charts one survey series can be drawn as.
 *
 * These are hand-rolled SVG in the same register as `components/charts/`, and they live
 * here rather than there for one reason: the baseline's charts are the only ones on the
 * portal whose row set is a *filter* rather than a fixed four countries, and whose scale is
 * a survey share with a reference line on it. Folding four more modes into `BarsH` would
 * have made the shared primitive answer to a section instead of the other way round.
 *
 * What they share with the rest of the portal is what a reader notices: the shared
 * `ChartTip` portal (§F2 — one tooltip node on the page), mono numbers, an em dash where a
 * survey measured nothing, and no bar at all for it.
 *
 * **Horizontal by default, vertical for income (client, two rounds, 2026-10-08).** The first
 * round removed `AmountChart` (vertical columns for an `amount` series) outright: *"make sure
 * every single chart is horizontal not vertical."* The second round's own chart-type table
 * asked for the opposite on this one series specifically — *"Income indicators: Use vertical
 * bar charts to compare average income across countries"* — so `AmountChart` is back, scoped
 * exactly as narrowly as that sentence: an `amount` series, in the countries view, four rows
 * or fewer. Every other shape this survey draws (shares, pairs, compositions, and an amount
 * over many watersheds, where thin columns stop being readable) stays on `BarsChart`,
 * horizontal, unchanged by either round.
 *
 * Colour (§G8 palette note, amended by the client on 7 Oct 2026 for this section only): these
 * charts do **not** use the country palette. Every chart is one hue, `BASELINE_HUE`, with its
 * rows sorted highest first (`sortRows`) and each bar tinted by its rank (`rankTint`): the
 * top bar full strength, lighter going down, the lowest still at 3:1 on the panel. The bars
 * carry the names, so there is no per-country key. The scope's own row is outlined rather
 * than recoloured. A composition's answers are shades of the same green, darkest first. Geometry
 * is in viewBox units and the SVG is stretched to the card with `width:100%`, which is how
 * every other chart in this codebase is responsive.
 *
 * The reading rules the client asked for — *"clear not compact"* — are constants here, so
 * they cannot drift between the three: a 28-unit bar, at least 18 units of air between two
 * of them, 11–12 unit labels, and a value printed beside every bar rather than read off an
 * axis.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { DashboardBaseline, DashboardBaselineSeries } from '../../lib/api';
import { AxisLabels } from '../../components/charts/AxisLabels';
import { ChartFrame } from '../../components/charts/ChartFrame';
import { useAxisTitles } from '../../components/charts/axisContext';
import { hideChartTip, showChartTip } from '../../components/charts/chartTipStore';
import {
  LABEL_LINE_H,
  VALUE_GAP,
  axisTicks,
  labelColumn,
  niceCeil,
  placeValue,
  valueAxis,
} from '../../components/charts/labels';
import { RowLabel } from '../../components/charts/RowLabel';
import { axisBands, axisTitle } from '../../components/charts/scale';
import { NO_VALUE, fmtNum } from '../../lib/format';
import { BASELINE_HUE, mixHex, rankTint } from '../../lib/palette';
import { chartFont, textWidth, wrapText } from '../../lib/text';
import {
  axisValue,
  baselineRefs,
  cardTitle,
  COMPOSITION_COLORS,
  COMPOSITION_LIGHT_FROM,
  fmtValue,
  hasEstimate,
  seriesDelta,
  sortRows,
  type BaselineAxisRow,
  type BaselineChartSpec,
  type BaselineView,
} from './baseline';

/* ---------- geometry (viewBox units; the SVG is scaled to the card) ---------- */

/**
 * The plot's own width — doubled from the original 560 (client, 2026-10-08: "reduce the size
 * of the components in the chart, they are too huge"). Every bar, gap and type size below is
 * in these same units and the `<svg>` is stretched to the card with `width:100%` (no change
 * there), so a card's *rendered* size is `BL_W`'s own units times whatever scale that stretch
 * works out to. 560 was tuned for the card at its old half-width, two-column measure, landing
 * near 1:1 — going full width (the round before this one) roughly doubled that measure
 * without doubling `BL_W` to match, so every component doubled with it. Measured on the live
 * render at 1440px: a 576-unit (`BL_W` plus the axis-label bands) chart stretched to 1202px,
 * a ×2.09 scale against the ×1.06–1.11 the numbers below were chosen at. Doubling `BL_W`
 * halves that scale back down to the range they were tuned for, at the width the chart
 * actually renders at now — nothing else here changes, because every other constant is
 * already written as a fraction of `BL_W`, not an absolute. */
export const BL_W = 1120;
/** One bar's thickness, and the air under it. "Clear not compact" is these two numbers.
 *  `BL_ROW_H` trimmed 28 -> 22 (client, 2026-10-08: "reduce the height of the bar a bit"). */
export const BL_ROW_H = 16;
export const BL_ROW_GAP = 20;
/**
 * The narrowest the row-name column gets. Since the launch round (7 Oct 2026) it grows to
 * the longest measured name, up to 45 % of the chart, and a longer name wraps onto two
 * lines; no name is cut with an ellipsis.
 */
export const BL_LABEL_W = 116;
/** The right margin past the plot. The value labels' room is measured into the axis. */
export const BL_PAD_RIGHT = 12;
export const BL_PAD_TOP = 12;
/** The x-axis strip under a horizontal chart. */
export const BL_AXIS_H = 24;
/** A floor, so a one-row chart still reads as a chart — and a ceiling on nothing else. */
export const BL_MIN_H = 96;
/** Type sizes: the row names, and the numbers beside the bars. */
export const BL_LABEL_SIZE = 12;
export const BL_VALUE_SIZE = 9.5;
/** A pair's smaller numbers (`.bl-num.sm`). */
const BL_VALUE_SM = 10.4;
/**
 * An amount chart is drawn as columns only while its categories fit across the card; past
 * this many rows (the watersheds view) it is drawn as rows, where every name and every
 * figure has its own line (client, 7 Oct 2026: "see crop yield, income, etc. by watershed").
 */
export const BL_AMOUNT_COLUMNS_MAX = 4;

/**
 * The rows' geometry: the measured label column, each row's own pitch (a wrapped name makes
 * its row taller), and the height the body needs — it *grows*, it does not sit in a fixed
 * frame. A country scope in the countries view is one row, and padding that out to the
 * height of a four-country chart left a card three-quarters empty (screenshot review,
 * round 1).
 */
interface RowLayout {
  plotX: number;
  plotW: number;
  lines: string[][];
  /** Top of each row's bar band. */
  ys: number[];
  /** Height of each row's bar band (the bar, or a taller wrapped name). */
  bands: number[];
  h: number;
  axisY: number;
}

function rowLayout(rows: BaselineAxisRow[]): RowLayout {
  const column = labelColumn(
    rows.map((r) => r.label),
    { size: BL_LABEL_SIZE, min: BL_LABEL_W, total: BL_W },
  );
  const bands = column.lines.map((lines) =>
    Math.max(BL_ROW_H, Math.ceil(lines.length * BL_LABEL_SIZE * LABEL_LINE_H)),
  );
  const ys: number[] = [];
  let y = BL_PAD_TOP;
  for (const band of bands) {
    ys.push(y);
    y += band + BL_ROW_GAP;
  }
  const body = rows.length === 0 ? BL_ROW_H + BL_ROW_GAP : y - BL_PAD_TOP;
  const h = Math.max(BL_MIN_H, BL_PAD_TOP + body + BL_AXIS_H);
  return {
    plotX: column.width,
    plotW: BL_W - column.width - BL_PAD_RIGHT,
    lines: column.lines,
    ys,
    bands,
    h,
    axisY: h - BL_AXIS_H,
  };
}

/** The bar's own top inside a band that may have grown for a two-line name. */
function barTop(layout: RowLayout, i: number, barH = BL_ROW_H): number {
  return layout.ys[i]! + (layout.bands[i]! - barH) / 2;
}

/**
 * The width of one bar — and **zero draws nothing**.
 *
 * The 2-unit floor exists so a 0.3 % bar is still visible rather than a hairline of nothing;
 * applying it to a measured zero would have drawn the same stub for "none of them do this"
 * as for "almost none", which is the one distinction a survey chart must not lose (review,
 * fix round 2). A `null` draws nothing either, and prints an em dash instead.
 */
function barLength(value: number | null, max: number, span: number): number {
  if (value === null || value === 0 || !Number.isFinite(value)) return 0;
  return Math.max(2, (Math.abs(value) / max) * span);
}

/** A printed figure's width, in the mono value font. */
function figureW(text: string, size = BL_VALUE_SIZE, weight = 700): number {
  return textWidth(text, size, weight, true);
}

/**
 * The value axis of a horizontal chart: room for every printed figure past its bar end,
 * moved past a reference line where it would cross it (`charts/labels.ts`). A share keeps
 * 100 as its floor and three ticks; an amount gets its own clean scale.
 */
function rowAxis(
  figures: { v: number | null; text: string; size?: number; weight?: number }[],
  refs: { value: number }[],
  plotW: number,
  share: boolean,
): { max: number; ticks: number[] } {
  return valueAxis({
    items: figures.map((f) => ({ v: f.v, w: figureW(f.text, f.size, f.weight) })),
    plotW,
    refs: refs.map((r) => r.value),
    floor: share ? 100 : 0,
    steps: share ? 2 : 4,
  });
}

/** An amount's printed figure: the number alone, because the axis title carries its unit. */
function amountText(value: number | null, fmt: number): string {
  return fmtValue(value, fmt, '');
}

/** Where a figure starts: past its bar, and past a reference line it would cross. */
function figureX(
  layout: RowLayout,
  barW: number,
  text: string,
  refs: { value: number }[],
  max: number,
  size = BL_VALUE_SIZE,
  weight = 700,
): number {
  const refXs = refs.map((r) => layout.plotX + (max > 0 ? Math.min(r.value / max, 1) : 0) * layout.plotW);
  return placeValue(layout.plotX + barW, figureW(text, size, weight), refXs);
}

/**
 * A pair's two bars are two shades of the row's **own** tint — the same country, asked
 * twice. Two hues would have said "two categories", and the categories here are the rows.
 *
 * Module-private: an exported array costs this file a `react-refresh` warning, and no other
 * file needs it.
 */
const PAIR_SHADE = [1, 0.55];

/**
 * Each sorted row's fill: its rank's tint of the one hue, over the rows that have a value.
 * The rows arrive sorted (`sortRows`), so the measured rows are the first `n` and a missing
 * estimate, which draws no bar, gets the neutral grey.
 */
function rankFills(values: (number | null)[]): string[] {
  const n = values.filter((v) => v !== null).length;
  let rank = 0;
  return values.map((v) => (v === null ? 'var(--line-2)' : rankTint(BASELINE_HUE, rank++, n)));
}

/** The scope's own row is outlined; every other bar has no stroke. */
function selClass(row: BaselineAxisRow): string | undefined {
  return row.selected ? 'bl-sel' : undefined;
}

export interface BaselineChartProps {
  spec: BaselineChartSpec;
  baseline: DashboardBaseline;
  rows: BaselineAxisRow[];
  view: BaselineView;
}

export interface BaselineChartCardProps extends BaselineChartProps {
  /** The sheet the card sits under — its name comes off the front of the card's title. */
  sheetLabel: string;
}

/* ---------- shared pieces ---------- */

/**
 * The chart's `<svg>`, with its frame's axis titles (launch round, 2026-10-07).
 *
 * The four charts below are fixed-width (`BL_W`); the titles' bands are added around that
 * width rather than taken out of it, so each chart's own geometry is untouched and drawn
 * one band to the right.
 */
function BlSvg({
  h,
  plot,
  label,
  children,
}: {
  h: number;
  plot: { left: number; right: number; top: number; bottom: number };
  label: string;
  children: ReactNode;
}) {
  const { xLabel, yLabel } = useAxisTitles();
  const bands = axisBands(xLabel, yLabel);
  return (
    <svg
      className="bl-chart"
      viewBox={`0 0 ${BL_W + bands.left} ${h + bands.bottom}`}
      role="img"
      aria-label={label}
    >
      <AxisLabels
        xLabel={xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + plot.left}
        plotRight={bands.left + plot.right}
        plotTop={plot.top}
        plotBottom={plot.bottom}
        height={h + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined}>{children}</g>
    </svg>
  );
}

/** One row's name, on one line or two, right-aligned against the plot. */
function RowName({ layout, i, label }: { layout: RowLayout; i: number; label: string }) {
  return (
    <RowLabel
      className="bl-rowlabel"
      lines={layout.lines[i] ?? [label]}
      x={layout.plotX - 8}
      cy={layout.ys[i]! + layout.bands[i]! / 2}
      size={BL_LABEL_SIZE}
      textAnchor="end"
      data-label-full={label}
    />
  );
}

/**
 * The bar's own track — the full plot width, drawn under every row before its value fill
 * (client, 2026-10-08: "let's have a gray outline for the bar to 100% then the green is like
 * a progressive bar in it"). The same track-then-fill idiom the rest of the portal already
 * uses for a share (`.progressbar`, `.exp-progress`, `.mgr-progress`, `.kring-track` — all
 * `var(--line-2)` under the brand colour), moved into SVG because these bars are drawn, not
 * styled. It is unconditional — even a row with no estimate at all gets the neutral track,
 * because the track is the scale's own 0-to-`max` reference, not a mark about that row's own
 * data; only the *value* fill stays conditional on there being a value (`barLength`'s own
 * "zero draws nothing, `null` draws nothing" rule, untouched). */
function BarTrack({ layout, y, width = layout.plotW, barH = BL_ROW_H }: { layout: RowLayout; y: number; width?: number; barH?: number }) {
  return <rect className="bl-track" x={layout.plotX} y={y} width={width} height={barH} rx={3} />;
}

/** The dashed programme/country markers, drawn under the bars and every printed figure. */
function RefLines({
  refs,
  max,
  top,
  height,
  layout,
}: {
  refs: { kind: string; value: number }[];
  max: number;
  top: number;
  height: number;
  layout: RowLayout;
}) {
  return (
    <g className="bl-refs">
      {refs.map((ref) => {
        const x = layout.plotX + (max > 0 ? Math.min(ref.value / max, 1) : 0) * layout.plotW;
        return (
          <line
            key={ref.kind}
            className={`bl-ref bl-ref-${ref.kind}`}
            x1={x}
            x2={x}
            y1={top}
            y2={top + height}
          />
        );
      })}
    </g>
  );
}

/**
 * The value axis under a horizontal chart: a share's 0 · 50 · 100 %, or an amount's clean
 * scale. The only axis a chart gets (§G8), and few marks, because every bar already prints
 * its own figure beside it: the axis is here to say *what the width means*.
 */
function ValueAxis({
  y,
  max,
  ticks,
  unit,
  fmt,
  layout,
}: {
  y: number;
  max: number;
  ticks: number[];
  unit: string;
  fmt: number;
  layout: RowLayout;
}) {
  return (
    <g className="bl-axis" aria-hidden="true">
      <line x1={layout.plotX} x2={layout.plotX + layout.plotW} y1={y} y2={y} />
      {ticks.map((tick, i) => {
        const atEnd = Math.abs(tick - max) < max * 1e-9;
        return (
          <text
            key={tick}
            x={layout.plotX + (max > 0 ? tick / max : 0) * layout.plotW}
            y={y + 14}
            textAnchor={i === 0 ? 'start' : atEnd ? 'end' : 'middle'}
          >
            {unit === '%' ? fmtValue(tick, fmt, unit) : fmtNum(tick, Number.isInteger(tick) ? 0 : fmt)}
          </text>
        );
      })}
    </g>
  );
}

/** One legend entry — a swatch (or a dashed rule) and a word. */
interface LegendItem {
  key: string;
  label: string;
  color: string;
  dashed?: boolean;
  /** Mirrors the bar's own fade, so a pair's two swatches are not one colour twice. */
  opacity?: number;
}

function Legend({ items }: { items: LegendItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="bl-legend">
      {items.map((item) => (
        <li key={item.key}>
          <span
            className={`bl-swatch${item.dashed ? ' dashed' : ''}`}
            style={
              item.dashed
                ? { borderColor: item.color }
                : { background: item.color, opacity: item.opacity ?? 1 }
            }
            aria-hidden="true"
          />
          {/* A renamed answer (2026-10-08: "Farmers reporting own land ownership (%)" for
              what used to read "Own property") runs well past the width a swatch row has to
              give it. `title` is the one tooltip every browser already knows how to draw, so
              a reader who needs the rest of it gets it without a second component — the CSS
              clips the line to one and lets the ellipsis say there is more. */}
          <span className="bl-legend-text" title={item.label}>
            {item.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The reference-line legend entries, which every comparison chart carries the same way. */
function useRefLegend(series: DashboardBaselineSeries, baseline: DashboardBaseline, view: BaselineView) {
  const { t } = useTranslation();
  return baselineRefs(series, baseline, view).map((ref) => ({
    key: `ref-${ref.kind}`,
    label: `${t(`dashboard.baseline.chart.${ref.kind}`)} · ${fmtValue(ref.value, series.fmt, series.unit)}`,
    color: ref.kind === 'programme' ? '#55614F' : '#C77B21',
    dashed: true,
  }));
}

/** Hover handlers for one datum, through the one tooltip node the page has. */
function tipProps(label: string, value: string) {
  return {
    onMouseMove: (e: { clientX: number; clientY: number }) => showChartTip(e, label, value),
    onMouseLeave: hideChartTip,
  };
}

/* ---------- 1. one value, one bar per row (a share, or an amount across many rows) ---------- */

function BarsChart({ spec, baseline, rows, view }: BaselineChartProps) {
  const series = spec.members[0]!.series;
  const share = spec.kind !== 'amount';
  const ordered = sortRows(rows, series, view);
  const refs = baselineRefs(series, baseline, view);
  const layout = rowLayout(ordered);
  const text = (value: number | null) => (share ? fmtValue(value, spec.fmt, spec.unit) : amountText(value, spec.fmt));
  const figures = ordered.map((r) => {
    const v = axisValue(series, r, view).value;
    return { v, text: text(v) };
  });
  // A share is read against 100; an axis fitted to the bars would make 12 % look like 80 %.
  const { max, ticks } = rowAxis(figures, refs, layout.plotW, share);
  const fills = rankFills(figures.map((f) => f.v));
  const { h, axisY } = layout;

  return (
    <BlSvg
      h={h}
      plot={{ left: layout.plotX, right: layout.plotX + layout.plotW, top: BL_PAD_TOP, bottom: axisY }}
      label={readout(spec, ordered, view)}
    >
      <ValueAxis y={axisY} max={max} ticks={ticks} unit={spec.unit} fmt={spec.fmt} layout={layout} />
      {/* Under the bars and the figures, not over them: a dashed rule drawn last crossed
          every printed figure on the chart (screenshot review, round 1). */}
      <RefLines refs={refs} max={max} top={BL_PAD_TOP - 6} height={axisY - BL_PAD_TOP + 6} layout={layout} />
      {ordered.map((row, i) => {
        const { value, n } = axisValue(series, row, view);
        const y = barTop(layout, i);
        const w = barLength(value, max, layout.plotW);
        const printed = figures[i]!.text;
        return (
          <g key={row.key} className="bl-row" {...tipProps(`${row.label}${n === null ? '' : ` · n=${fmtNum(n, 0)}`}`, fmtValue(value, spec.fmt, spec.unit))}>
            <title>{`${row.label} · ${fmtValue(value, spec.fmt, spec.unit)}`}</title>
            <RowName layout={layout} i={i} label={row.label} />
            <BarTrack layout={layout} y={y} />
            {w === 0 ? null : (
              <rect className={selClass(row)} x={layout.plotX} y={y} width={w} height={BL_ROW_H} rx={3} fill={fills[i]} />
            )}
            <text className="bl-num" x={figureX(layout, w, printed, refs, max)} y={y + BL_ROW_H / 2 + 4}>
              {printed}
            </text>
          </g>
        );
      })}
    </BlSvg>
  );
}

/* ---------- 2. a two-answer block: two bars per row ---------- */

function GroupedChart({ spec, baseline, rows, view }: BaselineChartProps) {
  const [first, second] = spec.members;
  const ordered = sortRows(rows, first!.series, view);
  const refs = baselineRefs(first!.series, baseline, view);
  const layout = rowLayout(ordered);
  const figures = spec.members.flatMap((m) =>
    ordered.map((r) => {
      const v = axisValue(m.series, r, view).value;
      return { v, text: fmtValue(v, spec.fmt, spec.unit), size: BL_VALUE_SM, weight: 600 };
    }),
  );
  const { max, ticks } = rowAxis(figures, refs, layout.plotW, true);
  // Ranked by the first answer, which is what the rows are sorted by.
  const fills = rankFills(ordered.map((r) => axisValue(first!.series, r, view).value));
  const { h, axisY } = layout;
  const barH = (BL_ROW_H - 4) / 2;

  return (
    <BlSvg
      h={h}
      plot={{ left: layout.plotX, right: layout.plotX + layout.plotW, top: BL_PAD_TOP, bottom: axisY }}
      label={readout(spec, ordered, view)}
    >
      <ValueAxis y={axisY} max={max} ticks={ticks} unit={spec.unit} fmt={spec.fmt} layout={layout} />
      <RefLines refs={refs} max={max} top={BL_PAD_TOP - 6} height={axisY - BL_PAD_TOP + 6} layout={layout} />
      {ordered.map((row, i) => {
        const y = barTop(layout, i);
        return (
          <g key={row.key} className="bl-row">
            <RowName layout={layout} i={i} label={row.label} />
            {[first!, second!].map((member, mi) => {
              const { value, n } = axisValue(member.series, row, view);
              const w = barLength(value, max, layout.plotW);
              const by = y + mi * (barH + 4);
              const text = fmtValue(value, spec.fmt, spec.unit);
              return (
                <g
                  key={member.series.id}
                  {...tipProps(`${row.label} · ${member.label}${n === null ? '' : ` · n=${fmtNum(n, 0)}`}`, text)}
                >
                  <title>{`${row.label} · ${member.label} · ${text}`}</title>
                  <BarTrack layout={layout} y={by} barH={barH} />
                  {w === 0 ? null : (
                    <rect
                      className={selClass(row)}
                      x={layout.plotX}
                      y={by}
                      width={w}
                      height={barH}
                      rx={2}
                      // A row whose first answer is missing still ranks last for its second.
                      fill={fills[i] === 'var(--line-2)' ? rankTint(BASELINE_HUE, ordered.length - 1, ordered.length) : fills[i]}
                      opacity={PAIR_SHADE[mi]!}
                    />
                  )}
                  <text
                    className="bl-num sm"
                    x={figureX(layout, w, text, refs, max, BL_VALUE_SM, 600)}
                    y={by + barH / 2 + 3.5}
                  >
                    {text}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </BlSvg>
  );
}

/* ---------- 3. a block that is a whole: one 100 % stack per row ---------- */

function StackedChart({ spec, rows: axisRows, view }: BaselineChartProps) {
  // Ranked by the first answer (owned land, say), highest first, like every baseline chart.
  const rows = sortRows(axisRows, spec.members[0]!.series, view);
  const layout = rowLayout(rows);
  const { h } = layout;

  return (
    <BlSvg
      h={h}
      plot={{ left: layout.plotX, right: layout.plotX + layout.plotW, top: BL_PAD_TOP, bottom: layout.axisY }}
      label={readout(spec, rows, view)}
    >
      {rows.map((row, i) => {
        const parts = spec.members.map((m) => ({
          member: m,
          value: axisValue(m.series, row, view).value,
        }));
        const total = parts.reduce((sum, p) => sum + (p.value ?? 0), 0);
        const y = barTop(layout, i);
        let x = layout.plotX;
        return (
          <g key={row.key} className="bl-row">
            <RowName layout={layout} i={i} label={row.label} />
            {row.selected && total > 0 ? (
              <rect className="bl-sel-frame" x={layout.plotX} y={y} width={layout.plotW} height={BL_ROW_H} />
            ) : null}
            {total <= 0 ? (
              <text className="bl-num" x={layout.plotX} y={y + BL_ROW_H / 2 + 4}>
                {NO_VALUE}
              </text>
            ) : (
              parts.map((part, pi) => {
                const share = (part.value ?? 0) / total;
                const w = share * layout.plotW;
                const sx = x;
                x += w;
                if (w <= 0) return null;
                const text = fmtValue(part.value, spec.fmt, spec.unit);
                return (
                  <g
                    key={part.member.series.id}
                    {...tipProps(`${row.label} · ${part.member.label}`, text)}
                  >
                    <title>{`${row.label} · ${part.member.label} · ${text}`}</title>
                    <rect
                      x={sx}
                      y={y}
                      width={Math.max(w - 1, 0.5)}
                      height={BL_ROW_H}
                      fill={COMPOSITION_COLORS[pi % COMPOSITION_COLORS.length]}
                    />
                    {w > 40 ? (
                      <text
                        className={`bl-seg${pi % COMPOSITION_COLORS.length >= COMPOSITION_LIGHT_FROM ? ' dark' : ''}`}
                        x={sx + w / 2}
                        y={y + BL_ROW_H / 2 + 4}
                        textAnchor="middle"
                      >
                        {fmtNum(share * 100, 0)}%
                      </text>
                    ) : null}
                  </g>
                );
              })
            )}
          </g>
        );
      })}
    </BlSvg>
  );
}


/**
 * The chart as one sentence, for a reader who cannot see it.
 *
 * Every figure a sighted reader gets off the bars is in here, because on this section the
 * chart is the *only* place the per-country and per-watershed numbers appear — the compact
 * rows that used to print them are gone.
 */
function readout(spec: BaselineChartSpec, rows: BaselineAxisRow[], view: BaselineView): string {
  const body = rows
    .map((row) =>
      spec.members
        .map((m) => {
          const value = fmtValue(axisValue(m.series, row, view).value, spec.fmt, spec.unit);
          return spec.members.length > 1 ? `${m.label} ${value}` : value;
        })
        .join(', '),
    )
    .map((text, i) => `${rows[i]!.label} ${text}`)
    .join('; ');
  return `${spec.title}: ${body}`;
}

/* ---------- 4. an amount across a few rows: columns with their own scale ---------- */

/**
 * Restored (client, 2026-10-08, second round: *"Income indicators: Use vertical bar charts to
 * compare average income across countries"*) — see the file's own docstring for why this
 * exists again and the one condition `BaselineChartCard` gates it on.
 */
function AmountChart({ spec, baseline, rows: axisRows, view }: BaselineChartProps) {
  const series = spec.members[0]!.series;
  const rows = sortRows(axisRows, series, view);
  const refs = baselineRefs(series, baseline, view);
  const values = rows.map((r) => axisValue(series, r, view).value);
  const fills = rankFills(values);
  const h = 230;
  const padLeft = 58;
  const padTop = 22;
  const slot = (BL_W - padLeft - 18) / Math.max(rows.length, 1);
  // A name wraps under its column rather than being cut; the plot gives up the line it takes.
  const font = chartFont(BL_LABEL_SIZE);
  const lines = rows.map((r) => wrapText(r.label, Math.max(24, slot - 6), font, 2));
  const extraLines = Math.max(1, ...lines.map((l) => l.length)) - 1;
  const padBottom = 30 + extraLines * BL_LABEL_SIZE * LABEL_LINE_H;
  const plotH = h - padTop - padBottom;
  const barW = Math.min(64, slot * 0.58);
  /*
    The figure above the tallest column needs its glyph height plus the gap, measured; the
    top padding already holds most of it, so the scale only grows by what is left.
  */
  const peak = Math.max(...values.map((v) => (v === null ? 0 : Math.abs(v))), ...refs.map((r) => r.value), 0);
  const above = Math.max(0, VALUE_GAP + BL_VALUE_SIZE * 0.75 - padTop);
  const axis = peak > 0 ? axisTicks(peak, niceCeil((peak * plotH) / (plotH - above)), 2) : { max: 1, ticks: [0, 0.5, 1] };
  const { max, ticks } = axis;
  const yTitle = useAxisTitles().yLabel;

  return (
    <BlSvg h={h} plot={{ left: padLeft, right: BL_W - 12, top: padTop, bottom: padTop + plotH }} label={readout(spec, rows, view)}>
      <g className="bl-axis" aria-hidden="true">
        {ticks.map((tick) => {
          const y = padTop + plotH - (tick / max) * plotH;
          return (
            <g key={tick}>
              <line x1={padLeft} x2={BL_W - 12} y1={y} y2={y} />
              <text x={padLeft - 8} y={y + 4} textAnchor="end">
                {fmtNum(tick, Number.isInteger(tick) ? 0 : spec.fmt)}
              </text>
            </g>
          );
        })}
        {/* The bare unit, rotated, only where the frame gave the chart no y title: the
            title already says it, as "Estimate (TLU)". */}
        {yTitle ? null : (
          <text className="bl-unit" transform={`translate(14 ${padTop + plotH / 2}) rotate(-90)`} textAnchor="middle">
            {spec.unit}
          </text>
        )}
      </g>
      {/* The reference lines first: under the columns and every printed figure. */}
      <RefLinesV refs={refs} max={max} left={padLeft} width={BL_W - padLeft - 12} top={padTop} plotH={plotH} />
      {rows.map((row, i) => {
        const value = values[i] ?? null;
        const barH = barLength(value, max, plotH);
        const x = padLeft + i * slot + (slot - barW) / 2;
        const y = padTop + plotH - barH;
        const text = amountText(value, spec.fmt);
        const full = fmtValue(value, spec.fmt, spec.unit);
        const n = axisValue(series, row, view).n;
        return (
          <g key={row.key} className="bl-row" {...tipProps(`${row.label}${n === null ? '' : ` · n=${fmtNum(n, 0)}`}`, full)}>
            <title>{`${row.label} · ${full}`}</title>
            {barH === 0 ? null : (
              <rect className={selClass(row)} x={x} y={y} width={barW} height={barH} rx={3} fill={fills[i]} />
            )}
            <text className="bl-num" x={x + barW / 2} y={y - VALUE_GAP} textAnchor="middle">
              {text}
            </text>
            <RowLabel
              className="bl-rowlabel"
              lines={lines[i]!}
              x={x + barW / 2}
              cy={padTop + plotH + 14 + ((lines[i]!.length - 1) * BL_LABEL_SIZE * LABEL_LINE_H) / 2}
              size={BL_LABEL_SIZE}
              textAnchor="middle"
              data-label-full={row.label}
            />
          </g>
        );
      })}
    </BlSvg>
  );
}

/** The amount chart's reference lines, which run across rather than down. */
function RefLinesV({
  refs,
  max,
  left,
  width,
  top,
  plotH,
}: {
  refs: { kind: string; value: number }[];
  max: number;
  left: number;
  width: number;
  top: number;
  plotH: number;
}) {
  return (
    <g className="bl-refs">
      {refs.map((ref) => {
        const y = top + plotH - (Math.min(ref.value / max, 1)) * plotH;
        return (
          <line key={ref.kind} className={`bl-ref bl-ref-${ref.kind}`} x1={left} x2={left + width} y1={y} y2={y} />
        );
      })}
    </g>
  );
}

/* ---------- 5. a summary heatmap: practices down, countries across ---------- */

/**
 * One table: every practice the sheet measures as its own share, one row each, with the
 * axis rows — always countries, since the picker only ever offers this kind in the countries
 * view — across the top (client, 2026-10-08: *"a summary heatmap, with practices as rows and
 * countries as columns"*).
 *
 * Plain HTML, not the SVG register every other chart in this file draws in: a heatmap is a
 * table of numbers first and a chart second, and a `<table>` is what a screen reader, a
 * find-in-page and a copy-paste into a spreadsheet already know how to read. Each cell's own
 * shade is `BASELINE_HUE` mixed toward white by how far the figure is from 100 % — the same
 * ramp `rankTint` uses elsewhere in this file, read as a continuous scale instead of a rank.
 */
function HeatmapChart({
  spec, rows, view,
}: {
  spec: BaselineChartSpec;
  rows: BaselineAxisRow[];
  view: BaselineView;
}) {
  return (
    <div className="bl-heatmap-wrap">
      <table className="bl-heatmap" data-testid="baseline-heatmap">
        <thead>
          <tr>
            <th scope="col" className="bl-heatmap-corner" />
            {rows.map((row) => (
              <th key={row.key} scope="col">
                {row.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.members.map((m) => (
            <tr key={m.series.id}>
              <th scope="row" title={m.label}>
                {m.label}
              </th>
              {rows.map((row) => {
                const { value } = axisValue(m.series, row, view);
                const frac = value === null ? null : Math.min(Math.max(value, 0), 100) / 100;
                return (
                  <td
                    key={row.key}
                    className={frac !== null && frac >= 0.5 ? 'bl-heatmap-dark' : undefined}
                    style={frac === null ? undefined : { background: mixHex(BASELINE_HUE, '#FFFFFF', 1 - frac) }}
                  >
                    {fmtValue(value, spec.fmt, spec.unit)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One card: a title, what the numbers are, a legend, and the chart.
 *
 * The card is the unit the client asked for — *"each sub-indicator with its own full
 * chart"* — so everything needed to read the chart is inside it and nothing is shared with
 * the card beside it except the axis the filter set.
 */
export function BaselineChartCard({
  spec,
  baseline,
  rows,
  view,
  sheetLabel,
}: BaselineChartCardProps) {
  const { t } = useTranslation();
  const series = spec.members[0]!.series;
  const refLegend = useRefLegend(series, baseline, view);
  const empty = rows.length === 0 || !hasEstimate(spec.members, rows, view);

  const legend =
    spec.kind === 'grouped' || spec.kind === 'stacked'
      ? spec.members.map((m, i) => ({
          key: m.series.id,
          label: m.label,
          color:
            spec.kind === 'stacked'
              ? COMPOSITION_COLORS[i % COMPOSITION_COLORS.length]!
              : BASELINE_HUE,
          // A pair's swatches carry the same two shades its bars do (review, fix round 2).
          opacity: spec.kind === 'grouped' ? PAIR_SHADE[i] ?? 1 : 1,
        }))
      : [];
  // A composition is normalised per row, so a programme line across it would be meaningless.
  // A heatmap's own colour scale is its legend (drawn inside `HeatmapChart`), not a swatch list.
  const items = spec.kind === 'stacked' || spec.kind === 'heatmap' ? legend : [...legend, ...refLegend];
  /* The axis titles: the rows are countries or watersheds; the values are a share of
     households, or for an amount the survey's estimate in its own unit. An amount over many
     rows (the watersheds view, or more than a handful of countries) is drawn as rows, like
     every other card — the client's "vertical bars for income" (2026-10-08) is a countries-
     view-with-few-rows chart, not a rule for every amount everywhere. */
  const amountAsRows = view === 'watersheds' || rows.length > BL_AMOUNT_COLUMNS_MAX;
  const columns = spec.kind === 'amount' && !amountAsRows;
  const rowTitle = t(view === 'watersheds' ? 'charts.axis.watershed' : 'charts.axis.country');
  const valueTitle =
    spec.kind === 'amount'
      ? axisTitle(t('dashboard.baseline.axis.estimate'), spec.unit)
      : axisTitle(t('dashboard.baseline.axis.share'), spec.unit || '%');
  const practiceTitle = t('dashboard.baseline.axis.practice');

  return (
    <div className="card bl-card" data-testid={`baseline-chart-${spec.id}`}>
      <div className="bl-card-head">
        {/* `h5` under the sheet's own `h4`, under the section's `h3`: the card is a level
            below the subsection it sits in, and a screen reader's outline says so. */}
        <h5 className="bl-card-title" title={spec.title}>
          {cardTitle(spec.title, sheetLabel)}
        </h5>
        {/* §3 — one per series the card draws, because a grouped card's two answers moved
            by different amounts and a single figure for both would belong to neither. A
            series with no earlier round prints nothing at all (`seriesDelta`). */}
        <Deltas spec={spec} />
      </div>
      <Legend items={items} />
      {empty ? (
        <p className="bl-empty" data-testid={`baseline-empty-${spec.id}`}>
          {t('dashboard.baseline.chart.none')}
        </p>
      ) : (
        <ChartFrame
          title={spec.title}
          subtitle={sheetLabel}
          legend={items.length > 0 ? <Legend items={items} /> : undefined}
          xLabel={spec.kind === 'heatmap' ? rowTitle : columns ? rowTitle : valueTitle}
          yLabel={spec.kind === 'heatmap' ? practiceTitle : columns ? valueTitle : rowTitle}
          testId={`baseline-chart-${spec.id}`}
        >
          {() =>
            spec.kind === 'heatmap' ? (
              <HeatmapChart spec={spec} rows={rows} view={view} />
            ) : spec.kind === 'stacked' ? (
              <StackedChart spec={spec} baseline={baseline} rows={rows} view={view} />
            ) : spec.kind === 'grouped' ? (
              <GroupedChart spec={spec} baseline={baseline} rows={rows} view={view} />
            ) : spec.kind === 'amount' && !amountAsRows ? (
              <AmountChart spec={spec} baseline={baseline} rows={rows} view={view} />
            ) : (
              <BarsChart spec={spec} baseline={baseline} rows={rows} view={view} />
            )
          }
        </ChartFrame>
      )}
    </div>
  );
}

/** The "since {{period}}" line under a card's title — one per member that has an earlier round. */
function Deltas({ spec }: { spec: BaselineChartSpec }) {
  const { t } = useTranslation();
  const deltas = spec.members.flatMap((m) => {
    const delta = seriesDelta(m.series);
    return delta ? [{ id: m.series.id, label: m.label, delta }] : [];
  });
  if (deltas.length === 0) return null;
  return (
    <p className="bl-card-deltas">
      {deltas.map(({ id, label, delta }) => (
        <span
          key={id}
          className={['bl-delta', delta.direction].filter(Boolean).join(' ')}
          data-testid={`baseline-delta-${id}`}
          /* Which answer moved, on a card that draws more than one of them. */
          title={[
            spec.members.length > 1 ? label : '',
            t('dashboard.baseline.deltaLabel', { period: delta.period }),
          ]
            .filter(Boolean)
            .join(' · ')}
        >
          {/*
            A share's change is in **percentage points** and says so: 30 % to 33.7 % is
            `+3.7 pp`, never `+3.7%`. An amount keeps its own unit inside `delta.text`.
          */}
          {t(delta.share ? 'dashboard.baseline.deltaShare' : 'dashboard.baseline.delta', {
            delta: delta.text,
            period: delta.period,
          })}
        </span>
      ))}
    </p>
  );
}

export default BaselineChartCard;
